import { type CacheBackend } from "@pdfking/compatibility"
import type { MessageRouter, ReceiverInput } from "@pdfking/functionpool"
import { CachedForward } from "@pdfking/functionpool"
import type { StringGUID } from "@pdfking/tfunc"
import { CreateSingleton, Peek, SafeIterable } from "@pdfking/tfunc"

import { AutoThrow } from "../errors/AutoThrow"
import type { APIRequest } from "../types/api/APIRequest"
import type { APIResponse } from "../types/api/APIResponse"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import { TaskName } from "../types/tasks/TaskName"
import type { TaskOptions } from "../types/tasks/TaskOptions"
import type { TrackingInterface } from "../types/tasks/tracking/TrackingInterface"
import { CreateEngineVirtual } from "./CreateEngineVirtual"

const emit_callbacks = (
  forward: MessageRouter<APIRequest, APIResponse>,
  tracking_callback: TrackingInterface | undefined = undefined
): MessageRouter<APIRequest, APIResponse> =>
  async function* (original) {
    const { guid, input } = await extract_task_id(original)

    tracking_callback?.start(guid)

    const { stream, error } = SafeIterable(forward(input))

    for await (const response of stream) {
      if (response.enum == TaskName.TRACKING) {
        tracking_callback?.push(guid, response)
      } else {
        yield response
      }
    }

    tracking_callback?.stop(guid, await error)

    await AutoThrow(error)
  }

const extract_task_id = (
  input: ReceiverInput<APIRequest>
): Promise<{
  guid: StringGUID
  input: ReceiverInput<APIRequest>
  cache_evict: boolean
}> =>
  Peek(input).then(({ value, stream }) => ({
    input: stream,
    guid: (value as TaskOptions).task_id,
    cache_evict: (value as TaskOptions).cache_evict,
  }))

export const CreateEngineMaster = (
  config: Partial<PdfEngineOptions>,
  factory: (
    router: MessageRouter<APIRequest, APIResponse>
  ) => MessageRouter<APIRequest, APIResponse>,
  cache: CacheBackend<APIResponse[]> | undefined = undefined,
  tracking_callback: TrackingInterface | undefined = undefined
): PdfEngine => {
  const router = CreateSingleton(() => factory(forward))

  const forward: MessageRouter<APIRequest, APIResponse> = CachedForward(
    emit_callbacks(t => router()(t), tracking_callback),
    extract_task_id,
    cache
  )

  return CreateEngineVirtual(config, forward)
}
